import { Box, Button, Link, Typography } from "@material-ui/core";

import usePresignedUrl from "../../hooks/usePresignedUrl";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function getContrastColor(hexColor) {
  if (!hexColor) {
    return undefined;
  }
  const hex = hexColor.replace("#", "");
  if (hex.length < 6) {
    return undefined;
  }
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? "rgba(0, 0, 0, 0.87)" : "#ffffff";
}

// ---------------------------------------------------------------------------
// Layout configuration
// ---------------------------------------------------------------------------

const TYPE_STYLES = {
  login_page: { textAlign: "left", titleVariant: "h6", contentVariant: "body1", singleLine: false },
  top_announcement: {
    textAlign: "center",
    titleVariant: "subtitle1",
    contentVariant: "body2",
    singleLine: true,
    actionAsLink: true,
  },
  modal: { textAlign: "left", titleVariant: "h6", contentVariant: "body1", singleLine: false },
};

const DEFAULT_TYPE_STYLE = TYPE_STYLES.top_announcement;

const IMAGE_SIZE = {
  top: { width: "100%", maxHeight: 220 },
  side: { width: 240, maxHeight: 240 }, // used for "left" / "right"
};

function getTypeStyle(type) {
  return TYPE_STYLES[type] || DEFAULT_TYPE_STYLE;
}

function isSideImage(imagePosition) {
  return imagePosition === "left" || imagePosition === "right";
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const SingleLineText = ({ title, content, titleVariant, contentVariant, textAlign, inlineAction }) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent={textAlign === "center" ? "center" : "flex-start"}
    minWidth={0}
  >
    {title && (
      <Typography variant={titleVariant} style={{ fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}>
        {title}
      </Typography>
    )}
    {title && content && <Box flexShrink={0} style={{ width: 8 }} />}
    {content && (
      <Typography
        variant={contentVariant}
        style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, maxWidth: "50%" }}
      >
        {content}
      </Typography>
    )}
    {inlineAction}
  </Box>
);

const MultiLineText = ({ title, content, titleVariant, contentVariant }) => (
  <>
    {title && (
      <Typography variant={titleVariant} style={{ fontWeight: 600 }}>
        {title}
      </Typography>
    )}
    {content && (
      <Typography variant={contentVariant} style={{ whiteSpace: "pre-wrap", marginTop: title ? 4 : 0 }}>
        {content}
      </Typography>
    )}
  </>
);

const AnnouncementActions = ({
  navigation,
  buttonLabel,
  buttonUrl,
  action,
  hasText,
  actionAsLink = false,
  hideButton = false,
  openNewPage = false,
  onActionClick,
}) => {
  const showButton = !hideButton && buttonLabel && buttonUrl;
  if (!navigation && !showButton && !action) {
    return null;
  }

  return (
    <Box
      mt={1}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      marginTop={hasText ? 4 : 0}
    >
      <Box display="flex" alignItems="center">
        {navigation}
      </Box>
      <Box display="flex" alignItems="center">
        {showButton &&
          (actionAsLink ? (
            <Link
              href={buttonUrl}
              target={openNewPage ? "_blank" : undefined}
              rel={openNewPage ? "noopener noreferrer" : undefined}
              onClick={onActionClick}
              color="inherit"
              style={{ cursor: "pointer", fontWeight: 600 }}
            >
              {buttonLabel}
            </Link>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              href={buttonUrl}
              target={openNewPage ? "_blank" : undefined}
              rel={openNewPage ? "noopener noreferrer" : undefined}
              onClick={onActionClick}
              style={{ padding: "4px 24px" }}
            >
              {buttonLabel}
            </Button>
          ))}
        {action && (
          <Box
            ml={showButton ? 1 : 0}
            display="flex"
            alignItems="center"
          >
            {action}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AnnouncementTextBlock = ({ announcement, style, navigation, action, onActionClick }) => {
  const { title, content, imagePosition = "top", buttonLabel, buttonUrl, openNewPage = false } = announcement;
  const { textAlign, titleVariant, contentVariant, singleLine, actionAsLink } = style;

  const TextComponent = singleLine ? SingleLineText : MultiLineText;

  const inlineAction =
    singleLine && actionAsLink && buttonLabel && buttonUrl ? (
      <Box flexShrink={0} ml={1} display="flex" alignItems="center">
        <Link
          href={buttonUrl}
          target={openNewPage ? "_blank" : undefined}
          rel={openNewPage ? "noopener noreferrer" : undefined}
          color="inherit"
          style={{ cursor: "pointer", fontWeight: 600 }}
        >
          {buttonLabel}
        </Link>
      </Box>
    ) : null;

  return (
    <Box flex={imagePosition === "top" ? "initial" : 1} minWidth={0}>
      <TextComponent
        title={title}
        content={content}
        titleVariant={titleVariant}
        contentVariant={contentVariant}
        textAlign={textAlign}
        inlineAction={inlineAction}
      />
      <AnnouncementActions
        navigation={navigation}
        buttonLabel={buttonLabel}
        buttonUrl={buttonUrl}
        action={action}
        actionAsLink={actionAsLink}
        hideButton={Boolean(inlineAction)}
        hasText={Boolean(title || content)}
        openNewPage={openNewPage}
        onActionClick={onActionClick}
      />
    </Box>
  );
};

const AnnouncementImage = ({ src, alt, imagePosition, textAlign }) => {
  if (!src) {
    return null;
  }

  const size = isSideImage(imagePosition) ? IMAGE_SIZE.side : IMAGE_SIZE.top;

  return (
    <Box
      display="flex"
      justifyContent={textAlign === "center" ? "center" : "flex-start"}
      mb={2}
      mr={imagePosition === "left" ? 2 : undefined}
      ml={imagePosition === "right" ? 2 : undefined}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={src}
        alt={alt || "announcement"}
        loading="lazy"
        decoding="async"
        style={{ ...size, objectFit: "cover", borderRadius: 8 }}
      />
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AnnouncementContent = ({ announcement, navigation, action, onActionClick }) => {
  const presignedUrl = usePresignedUrl("announcement_image", announcement?.imageUrl);

  if (!announcement) {
    return null;
  }

  const { title, imagePosition = "top" } = announcement;
  const style = getTypeStyle(announcement.type);

  const image = (
    <AnnouncementImage
      src={presignedUrl}
      alt={title}
      imagePosition={imagePosition}
      textAlign={style.textAlign}
    />
  );

  const textBlock = (
    <AnnouncementTextBlock announcement={announcement} style={style} navigation={navigation} action={action} onActionClick={onActionClick} />
  );

  if (isSideImage(imagePosition)) {
    return (
      <Box
        display="flex"
        flexDirection={imagePosition === "left" ? "row" : "row-reverse"}
        alignItems="center"
        textAlign={style.textAlign}
        height="100%"
      >
        {image}
        {textBlock}
      </Box>
    );
  }

  return (
    <Box textAlign={style.textAlign}>
      {image}
      {textBlock}
    </Box>
  );
};

export default AnnouncementContent;